import { useAccount, useReadContract } from "wagmi";
import { PIGGY_STAKING_ABI, LOCKED_LP_STAKING_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

// Staking threshold that unlocks the premium Radar sections: 1M PIGGY (18 dec).
export const RADAR_STAKE_THRESHOLD = 1_000_000n * 10n ** 18n;

const PAIR_ABI = [
    { type: "function", name: "getReserves", stateMutability: "view", inputs: [],
      outputs: [{ type: "uint112" }, { type: "uint112" }, { type: "uint32" }] },
    { type: "function", name: "token0", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
    { type: "function", name: "totalSupply", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
] as const;

export interface RadarAccess {
    unlocked: boolean;
    stakedPiggy: bigint;     // direct PIGGY stake
    lpStaked: bigint;        // raw LP tokens staked
    lpPiggyEquiv: bigint;    // the PIGGY those LP tokens represent
    threshold: bigint;
    connected: boolean;
}

/// Client-side gating check for Piggy Radar. Reads the connected wallet's
/// staked PIGGY from the staking contract AND the PIGGY-equivalent of any
/// locked-LP stake (lpStaked × pairPiggyReserve ÷ pairTotalSupply — the PIGGY
/// side of the LP position). Direct stake + LP equivalent ≥ threshold unlocks,
/// so providing liquidity counts the same as staking the tokens outright.
/// The threshold is USD-pegged and passed in live (`stakeThresholdTokens`,
/// whole PIGGY), falling back to a 1M default. A server session (from
/// useRadarAuth) also counts as unlocked via `sessionUnlocked`.
export function useRadarAccess(sessionUnlocked = false, stakeThresholdTokens?: number): RadarAccess {
    const { address } = useAccount();
    const staking = ADDRESSES.PIGGY_STAKING;
    const connected = Boolean(address);

    const { data } = useReadContract({
        address: staking,
        abi: PIGGY_STAKING_ABI,
        functionName: "userStateOf",
        args: address ? [address] : undefined,
        query: { enabled: Boolean(staking && address), refetchInterval: 30_000 },
    });

    const lpStaking = ADDRESSES.LOCKED_LP_STAKING;
    const { data: lpData } = useReadContract({
        address: lpStaking,
        abi: LOCKED_LP_STAKING_ABI,
        functionName: "userStakeOf",
        args: address ? [address] : undefined,
        query: { enabled: Boolean(lpStaking && address), refetchInterval: 30_000 },
    });

    // Pair state to value the LP stake in PIGGY terms. Only fetched while a
    // wallet is connected and has LP staked.
    const pair = ADDRESSES.PIGGY_PAIR;
    const lpStaked = (lpData as { amount: bigint } | undefined)?.amount ?? 0n;
    const pairReadsOn = Boolean(pair && address && lpStaked > 0n);
    const { data: reserves } = useReadContract({
        address: pair, abi: PAIR_ABI, functionName: "getReserves",
        query: { enabled: pairReadsOn, refetchInterval: 60_000 },
    });
    const { data: token0 } = useReadContract({
        address: pair, abi: PAIR_ABI, functionName: "token0",
        query: { enabled: pairReadsOn, staleTime: Infinity },
    });
    const { data: lpSupply } = useReadContract({
        address: pair, abi: PAIR_ABI, functionName: "totalSupply",
        query: { enabled: pairReadsOn, refetchInterval: 60_000 },
    });

    const threshold = stakeThresholdTokens && stakeThresholdTokens > 0
        ? BigInt(Math.ceil(stakeThresholdTokens)) * 10n ** 18n
        : RADAR_STAKE_THRESHOLD;
    const stakedPiggy = (data as { stakedAmount: bigint } | undefined)?.stakedAmount ?? 0n;

    let lpPiggyEquiv = 0n;
    const piggy = ADDRESSES.PIGGY_TOKEN?.toLowerCase();
    if (lpStaked > 0n && reserves && token0 && lpSupply && (lpSupply as bigint) > 0n) {
        const [r0, r1] = reserves as unknown as [bigint, bigint, number];
        const piggyReserve = (token0 as string).toLowerCase() === piggy ? r0 : r1;
        lpPiggyEquiv = (lpStaked * piggyReserve) / (lpSupply as bigint);
    }

    const stakeUnlocked = connected && stakedPiggy + lpPiggyEquiv >= threshold;

    return {
        unlocked: sessionUnlocked || stakeUnlocked,
        stakedPiggy,
        lpStaked,
        lpPiggyEquiv,
        threshold,
        connected,
    };
}
